import { AlertCircle, CreditCard, Lock, User } from 'lucide-react';
import { CONTROL_SIZE } from '@/components/ui/types/control-enums';
import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { INPUT_APPEARANCE } from '@/components/ui/types/input-variants-enums';
import { Input } from '@/components/ui/input';
import { CustomerLogoGrid } from '@/features/signup/components/customer-logo-grid';
import { CUSTOMER_LOGO_GRID_SIZE } from '@/features/signup/types/customer-logo-grid-enums';
import { TrustBadges } from '@/components/marketing/trust-badges';
import { TRUST_BADGES_ORIENTATION, TRUST_BADGES_SIZE } from '@/components/marketing/types/trust-badges-enums';
import {
  ANALYTICS_EVENT,
} from '@/lib/analytics/types/analytics-enums';
import { PRIVACY_POLICY_URL, TERMS_URL } from '@/lib/constants/external-urls-constants';
import { useTracking } from '@/hooks/analytics/use-tracking';
import { useRegistrationForm } from '@/features/signup/hooks/use-registration-form';
import { EMAIL_STATUS } from '@/features/signup/types/use-registration-form-enums';
import { useSignupFlowStore } from '@/features/signup/stores/use-signup-flow-store';

import { PasswordFieldGroup } from './components/password-field-group';
import { SignupEmailField } from './components/signup-email-field';
import { SignupFormPanel, SignupPageIntro } from './components/signup-page-intro';
import { SignupRating } from './components/signup-rating';
import { SignupSplitLayout } from './components/signup-split-layout';
import { SocialProofPanel } from './components/social-proof-panel';
import { SubmitButton } from './components/submit-button';

const FORM_TRUST_BADGES = [
  {
    icon: <Lock className="h-full w-full" aria-hidden="true" />,
    label: 'SSL Secured',
  },
  {
    icon: <CreditCard className="h-full w-full" aria-hidden="true" />,
    label: 'Stripe',
    tooltip: 'Payments secured by Stripe',
  },
];

export function SignUp() {
  const [searchParams] = useSearchParams();
  const setPlanId = useSignupFlowStore(s => s.setPlanId);

  const {
    form,
    setEmailStatus,
    isSubmitting,
    submitError,
    canSubmit,
    handleSubmit,
  } = useRegistrationForm();

  const {
    register,
    control,
    formState: { errors },
    clearErrors,
  } = form;

  const planId = searchParams.get('planId');

  useTracking(ANALYTICS_EVENT.SIGNUP_PAGE_VIEWED, { planId: planId ?? undefined });

  useEffect(() => {
    if (planId) {
      setPlanId(planId);
    }
  }, [planId, setPlanId]);

  useEffect(() => {
    void import('./sign-up-otp');
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter' && canSubmit) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleEmailAvailabilityChange = (available: boolean) => {
    setEmailStatus(available ? EMAIL_STATUS.AVAILABLE : EMAIL_STATUS.TAKEN);
  };

  const firstNameReg = register('firstName', { required: 'First name is required' });
  const lastNameReg = register('lastName', { required: 'Last name is required' });

  return (
    <SignupSplitLayout socialProof={<SocialProofPanel />}>
      <SignupPageIntro>
        <SignupRating className="lg:hidden" />
      </SignupPageIntro>

      <SignupFormPanel className="mt-5">
        <form onSubmit={handleSubmit} onKeyDown={handleKeyDown} className="grid gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="grid gap-1">
              <Input
                appearance={INPUT_APPEARANCE.DEFAULT}
                size={CONTROL_SIZE.COMFORTABLE}
                id="firstName"
                placeholder="First name"
                aria-label="First name"
                aria-invalid={errors.firstName ? true : undefined}
                aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                type="text"
                autoComplete="given-name"
                leftIcon={<User className="h-4 w-4" aria-hidden="true" />}
                {...firstNameReg}
              />
              {errors.firstName ? (
                <span
                  id="firstName-error"
                  className="type-field-description text-status-danger-foreground"
                  role="alert"
                >
                  {errors.firstName.message}
                </span>
              ) : null}
            </div>
            <div className="grid gap-1">
              <Input
                appearance={INPUT_APPEARANCE.DEFAULT}
                size={CONTROL_SIZE.COMFORTABLE}
                id="lastName"
                placeholder="Last name"
                aria-label="Last name"
                aria-invalid={errors.lastName ? true : undefined}
                aria-describedby={errors.lastName ? 'lastName-error' : undefined}
                type="text"
                autoComplete="family-name"
                leftIcon={<User className="h-4 w-4" aria-hidden="true" />}
                {...lastNameReg}
              />
              {errors.lastName ? (
                <span
                  id="lastName-error"
                  className="type-field-description text-status-danger-foreground"
                  role="alert"
                >
                  {errors.lastName.message}
                </span>
              ) : null}
            </div>
          </div>

          <SignupEmailField
            control={control}
            name="email"
            onAvailabilityChange={handleEmailAvailabilityChange}
          />

          <PasswordFieldGroup
            control={control}
            register={register}
            errors={errors}
            clearErrors={clearErrors}
          />

          {/* Commit group: 32px from the field group, 16px within — better-layout §1. */}
          <div className="grid gap-4 pt-4">
            {submitError ? (
              <Alert variant="destructive" aria-live="assertive">
                <AlertCircle className="h-4 w-4" aria-hidden="true" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}

            <p className="type-body text-center text-content-primary">
              Free trial included · Cancel anytime
            </p>

            <SubmitButton loading={isSubmitting} disabled={!canSubmit}>
              Start Free Trial
            </SubmitButton>

            <TrustBadges
              badges={FORM_TRUST_BADGES}
              orientation={TRUST_BADGES_ORIENTATION.HORIZONTAL}
              size={TRUST_BADGES_SIZE.SM}
              className="justify-center"
            />

            <p className="text-center text-xs text-content-tertiary">
              By continuing, you agree to our{' '}
              <a
                href={TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Privacy Policy
              </a>
            </p>
          </div>
        </form>
      </SignupFormPanel>

      <div className="mt-8 lg:hidden">
        <CustomerLogoGrid size={CUSTOMER_LOGO_GRID_SIZE.SM} centered />
      </div>

      <p className="type-body mt-6 text-center text-content-primary">
        Already have an account?{' '}
        <Link to="/login" className="font-semibold text-primary hover:underline">
          Sign in
        </Link>
      </p>
    </SignupSplitLayout>
  );
}

export default SignUp;
